import { Microphone2, VolumeHigh } from "iconsax-reactjs";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DIAGNOSTIC_STEP_AUDIO_KEY, DIAGNOSTIC_STEP_AUDIO_VALUE, DIAGNOSTIC_STEP_BROWSER_SUPPORT_VALUE } from "@/constants/diagnostics";
import useAudioTest from "@/hooks/useAudioTest";
import { useIsSM } from "@/hooks/useIsSM";
import { selectDiagnosticsStep } from "@/store/features/diagnostics/selectors";
import { setDiagnosticsStep } from "@/store/features/diagnostics/slice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { Button } from "@/ui";
import { isSetSinkIdSupported } from "@/utils/diagnostics";
import AudioDevice from "../AudioDevice";
import DiagnosticsSection from "../DiagnosticsSection";
import { StyledAudioLevel, StyledAudioLevelProgress } from "./styled";

const Audio = () => {
  const { t } = useTranslation();

  const isSM = useIsSM();

  const dispatch = useAppDispatch();
  const step = useAppSelector(selectDiagnosticsStep);

  const [inputDeviceId, setInputDeviceId] = useState('');
  const [outputDeviceId, setOutputDeviceId] = useState('');

  const previousInputDeviceIdRef = useRef('');

  const {
    error,
    setError,
    isRecording,
    isAudioInputTestRunning,
    isAudioOutputTestRunning,
    playAudio,
    playbackURI,
    readAudioInput,
    stopAudioTest,
    inputLevel,
    outputLevel,
  } = useAudioTest();

  const volumeLevel = isAudioOutputTestRunning ? outputLevel : inputLevel;

  const disableAll = isRecording || isAudioOutputTestRunning || (!!error && error !== 'No audio detected');

  const handleRecordClick = () => {
    readAudioInput({ deviceId: inputDeviceId, enableRecording: true });
  };

  const handlePlayClick = () => {
    const options: { deviceId?: string; testURI: string } = { testURI: playbackURI };
    if (isSetSinkIdSupported()) {
      options.deviceId = outputDeviceId;
    }
    playAudio(options);
  };

  // stop test when not on AudioTest and there's an active test
  useEffect(() => {
    if (step !== DIAGNOSTIC_STEP_AUDIO_VALUE && (isAudioOutputTestRunning || isAudioInputTestRunning)) {
      stopAudioTest();
    }
  }, [isAudioInputTestRunning, isAudioOutputTestRunning, step, stopAudioTest]);

  // start audio test when on AudioTest and deviceId changes
  useEffect(() => {
    if (step === DIAGNOSTIC_STEP_AUDIO_VALUE) {
      const newInputDeviceSelected = previousInputDeviceIdRef.current !== inputDeviceId;
      previousInputDeviceIdRef.current = inputDeviceId;

      // Restarts the test to continuously capture audio input
      if (!error && (newInputDeviceSelected || (!isRecording && !isAudioInputTestRunning))) {
        readAudioInput({ deviceId: inputDeviceId });
      }
    }
    if (error) {
      stopAudioTest();
    }
  }, [error, inputDeviceId, isAudioInputTestRunning, isRecording, readAudioInput, step, stopAudioTest]);

  const onClickNext = () => {
    dispatch(setDiagnosticsStep(DIAGNOSTIC_STEP_BROWSER_SUPPORT_VALUE));
  };

  const preview = (
    <div className="section-frame">
      <h4 className="section-frame-title-with-buttons">
        {t('audio')}
        <div className="section-frame-title-buttons">
          <button onClick={handleRecordClick} disabled={disableAll}>{t('record')}</button>
          <button onClick={handlePlayClick} disabled={!playbackURI || disableAll}>{t('play')}</button>
        </div>
      </h4>
      <AudioDevice
        disabled={disableAll}
        kind="audiooutput"
        onDeviceChange={setOutputDeviceId}
        setDeviceError={setError}
      />
      <AudioDevice
        disabled={disableAll}
        kind="audioinput"
        onDeviceChange={setInputDeviceId}
        setDeviceError={setError}
        error={error}
      />
      <StyledAudioLevel>

        {isAudioOutputTestRunning ? (
          <VolumeHigh
            size="18"
            color="var(--color-primary)"
          />
        ) : (
          <Microphone2
            size="18"
            color="var(--color-primary)"
          />
        )}
        <StyledAudioLevelProgress $level={volumeLevel} />
      </StyledAudioLevel>
    </div>
  );

  return (
    <DiagnosticsSection id={DIAGNOSTIC_STEP_AUDIO_KEY}>
      <div className="section-wrapper">
        <div className="section-content">
          <h2>{t('test_your_audio')}</h2>
          <p>{t('test_your_audio_description_1')}</p>
          {isSM && preview}
          <p>
            <b>{t('test_your_audio_description_2')}</b>
          </p>
          <div className="section-buttons">
            <Button onClick={onClickNext}>{t('yes')}</Button>
            <Button variant="link" onClick={onClickNext}>{t('skip_for_now')}</Button>
          </div>
        </div>
        {!isSM && preview}
      </div>
    </DiagnosticsSection>
  );
};

export default Audio;